import type { FC } from "react"
import { Link, useNavigate } from "react-router-dom"

import styles from "./CompanyListTable.module.scss"

import { FragmentType, getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

// enabledFeatureFlagIds: 会社全体 ON のフラグ ID のみ。個別設定（サロン別 ON/OFF）は含まない。
const CompanyListTableFragment = graphql(/* GraphQL */ `
	fragment CompanyListTable on Company {
		id
		name
		enabledFeatureFlagIds
	}
`)

const CompanyListTableFeatureFlagFragment = graphql(/* GraphQL */ `
	fragment CompanyListTableFeatureFlag on FeatureFlag {
		id
		name
	}
`)

type CompanyListTableProps = {
	companies: FragmentType<typeof CompanyListTableFragment>[]
	featureFlags: FragmentType<typeof CompanyListTableFeatureFlagFragment>[]
}

const renderFlagBadge = (isEnabled: boolean) => {
	if (isEnabled)
		return <span className={styles.badgeEnabled}>会社全体設定</span>
	return <span className={styles.badgePerSalon}>店舗個別設定</span>
}

const CompanyListTable: FC<CompanyListTableProps> = ({
	companies,
	featureFlags,
}) => {
	const navigate = useNavigate()
	const companiesData = companies.map((company) =>
		getFragmentData(CompanyListTableFragment, company)
	)
	const flagsData = featureFlags.map((ff) =>
		getFragmentData(CompanyListTableFeatureFlagFragment, ff)
	)

	const handleCreateClick = () => {
		navigate("/souhl_admin/companies/create")
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>会社管理</h1>
				<button className={styles.createButton} onClick={handleCreateClick}>
					+ 新規作成
				</button>
			</div>

			<div className={styles.tableWrapper}>
				<h2 className={styles.tableTitle}>会社一覧</h2>
				<div className={styles.tableScroll}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.headerCellId}>ID</th>
								<th className={styles.headerCellName}>名前</th>
								{flagsData.map((flag) => (
									<th key={flag.id} className={styles.headerCellFlag}>
										{flag.name}
									</th>
								))}
								<th className={styles.headerCellAction}>操作</th>
							</tr>
						</thead>
						<tbody>
							{companiesData.map((company) => (
								<tr key={company.id} className={styles.row}>
									<td className={styles.cellId}>{company.id}</td>
									<td className={styles.cellName}>{company.name}</td>
									{flagsData.map((flag) => {
										const isEnabled = company.enabledFeatureFlagIds.includes(
											flag.id
										)
										return (
											<td key={flag.id} className={styles.cellFlag}>
												{renderFlagBadge(isEnabled)}
											</td>
										)
									})}
									<td className={styles.cellAction}>
										<Link
											to={`/souhl_admin/companies/${company.id}`}
											className={styles.link}
										>
											詳細
										</Link>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export default CompanyListTable
